import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import trackClick from 'js/utils/trackClick';

import {
  acknowledgeSeenValue,
  dotVisible,
  readSeenValues,
} from './acknowledgment';
import type { SeenMetric, SeenValues } from './acknowledgment';
import { impressionExtra, resolveNavImpactStats } from './arms';
import type { StatLayout, StatStyle } from './arms';
import type { ImpactStats } from '../types';
import ImpactStatTile from './ImpactStatTile';
import TrendAdornment from './ornaments/TrendAdornment';
import type { TrendMetric, TrendVariant } from './ornaments/TrendAdornment';

export const NAV_IMPACT_STRIP_WIDTH = 140;

type NavImpactStatsProps = {
  arm: string | null | undefined;
  impactStats?: ImpactStats | null;
  isMobileNav?: boolean;
  testRunning?: boolean;
};

const StripContainer = styled.div<{ isMobileNav?: boolean }>`
  flex: 0 0 auto;
  width: ${NAV_IMPACT_STRIP_WIDTH}px;
  min-width: ${NAV_IMPACT_STRIP_WIDTH}px;
  height: 48px;
  overflow: hidden;

  &.isMobileNav {
    height: 40px;
    flex-shrink: 1;
    min-width: 0;
    max-width: 40%;
  }
`;

const StripWatcher = styled(ImpressionWatcher)`
  width: 100%;
  height: 100%;
`;

const TileRow = styled.div<{ statLayout: StatLayout }>`
  display: flex;
  width: 100%;
  height: 100%;

  &.statLayout-stacked {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    justify-content: safe center;
  }

  &.statLayout-inline {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
`;

const CellDivider = styled.span`
  flex: 0 0 auto;
  width: 1px;
  height: 24px;
  margin: 0 1px;
  background: var(--background-grey, #dddde2);
`;

const TREND_VARIANTS: Partial<Record<StatStyle, TrendVariant>> = {
  delta_chip: 'chip',
  trend_icon: 'icon',
};

function trendAdornment(
  statStyle: StatStyle,
  pct: number | null,
  metric: TrendMetric,
): React.ReactNode {
  const variant = TREND_VARIANTS[statStyle];
  if (variant == null || pct == null) {
    return undefined;
  }
  return <TrendAdornment variant={variant} pct={pct} metric={metric} />;
}

const NavImpactStats = ({
  arm,
  impactStats,
  isMobileNav = false,
  testRunning = false,
}: NavImpactStatsProps) => {
  const [seenValues, setSeenValues] = useState<SeenValues | null>({});

  useEffect(() => {
    setSeenValues(readSeenValues());
  }, []);

  const surfaces = resolveNavImpactStats(arm);
  if (surfaces.statStyle === 'none') {
    return null;
  }

  const citations = impactStats?.citations ?? null;
  const paperViews = impactStats?.paperViews30d ?? null;
  const paperViewsTotal = impactStats?.paperViewsTotal ?? null;
  const hasData = citations != null || paperViews != null;
  const bucket = arm ?? 'control';

  const acknowledge = (metric: SeenMetric, value: number | null) => {
    if (value == null) {
      return;
    }
    const merged = acknowledgeSeenValue(metric, value);
    if (merged != null) {
      setSeenValues(merged);
    }
  };

  return (
    <StripContainer data-testid="nav-impact-stats" isMobileNav={isMobileNav}>
      {hasData && (
        <StripWatcher
          source="nav_impact_stats.strip"
          extra={impressionExtra(arm, testRunning)}
        >
          <TileRow statLayout={surfaces.statLayout}>
            <ImpactStatTile
              count={citations}
              label="Citations"
              layout={surfaces.statLayout}
              statStyle={surfaces.statStyle}
              adornment={trendAdornment(
                surfaces.statStyle,
                impactStats?.citationsTrendPct ?? null,
                'citations',
              )}
              href={routes.citations_url({
                from_navbar: true,
                trigger: 'nav_impact_stats',
              })}
              onClick={() => {
                trackClick({ source: 'nav-bar--impact-citations', bucket });
                acknowledge('citations', citations);
              }}
              showDot={dotVisible(seenValues, 'citations', citations)}
            />
            {surfaces.statLayout === 'stacked' &&
              citations != null &&
              paperViews != null && (
                <CellDivider data-testid="impact-cell-divider" />
              )}
            <ImpactStatTile
              count={paperViews}
              label="Paper views"
              layout={surfaces.statLayout}
              statStyle={surfaces.statStyle}
              adornment={trendAdornment(
                surfaces.statStyle,
                impactStats?.paperViewsTrendPct ?? null,
                'views',
              )}
              href="/analytics"
              onClick={() => {
                trackClick({ source: 'nav-bar--impact-paper-views', bucket });
                acknowledge('paperViewsTotal', paperViewsTotal);
              }}
              showDot={dotVisible(
                seenValues,
                'paperViewsTotal',
                paperViewsTotal,
              )}
            />
          </TileRow>
        </StripWatcher>
      )}
    </StripContainer>
  );
};

export default NavImpactStats;
